// src/pages/surveys/SurveyBuilder.js
import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── Tiny rich-text toolbar (B / I / U / line break) ─────────────────────────
// Inject contentEditable placeholder CSS once
let _richTextStyleInjected = false;
function injectRichTextStyle() {
  if (_richTextStyleInjected || typeof document === 'undefined') return;
  _richTextStyleInjected = true;
  const el = document.createElement('style');
  el.textContent = `[contenteditable][data-placeholder]:empty:before{content:attr(data-placeholder);color:#aaa;pointer-events:none;}`;
  document.head.appendChild(el);
}

function RichTextEditor({ value, onChange, placeholder, style, singleLine = false }) {
  const ref = useRef(null);
  const lastHtml = useRef(value || '');
  injectRichTextStyle();

  // Sync external value → DOM (only when it differs, to avoid cursor jump)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
      lastHtml.current = value || '';
    }
  }, [value]);

  const exec = (cmd, val) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    const html = ref.current?.innerHTML || '';
    lastHtml.current = html;
    onChange(html);
  };

  const handleInput = () => {
    const html = ref.current?.innerHTML || '';
    lastHtml.current = html;
    onChange(html);
  };

  const handleKeyDown = (e) => {
    if (singleLine && e.key === 'Enter') { e.preventDefault(); return; }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak', false, null);
    }
  };

  const btnStyle = (active) => ({
    padding: '2px 7px', fontSize: 12, fontWeight: 700,
    background: active ? 'var(--ka-navy)' : 'var(--ka-gray-100)',
    color: active ? 'white' : 'var(--ka-gray-700)',
    border: '1px solid var(--ka-gray-200)', borderRadius: 4,
    cursor: 'pointer', fontFamily: 'var(--font-body)', lineHeight: '18px',
  });

  return (
    <div style={{ border: '1px solid var(--ka-gray-200)', borderRadius: 6, overflow: 'hidden', ...style }}>
      <div style={{ display: 'flex', gap: 3, padding: '4px 6px', background: 'var(--ka-gray-50)', borderBottom: '1px solid var(--ka-gray-200)' }}>
        <button type="button" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('bold'); }} title="Bold"><b>B</b></button>
        <button type="button" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('italic'); }} title="Italic"><i>I</i></button>
        <button type="button" style={btnStyle(false)} onMouseDown={e => { e.preventDefault(); exec('underline'); }} title="Underline"><u>U</u></button>
        {!singleLine && (
          <button type="button" style={{ ...btnStyle(false), fontSize: 10 }} onMouseDown={e => { e.preventDefault(); exec('insertLineBreak'); }} title="Line break">↵</button>
        )}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        style={{
          padding: '8px 10px', minHeight: singleLine ? 36 : 60,
          fontSize: 14, fontFamily: 'var(--font-body)', lineHeight: 1.5,
          color: 'var(--ka-gray-800)', outline: 'none',
          background: 'var(--ka-white)',
        }}
      />
    </div>
  );
}

// Strip HTML to plain text (used for display in builder preview)
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
}
import { useNavigate, useParams } from 'react-router-dom';
import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp, collection
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import QuestionEditor, { QuestionPreview } from './components/QuestionEditor';
import SurveySettingsPanel from './components/SurveySettingsPanel';

const QUESTION_TYPES = [
  { type: 'short_text',    label: 'Short Text',      icon: '✏️' },
  { type: 'long_text',     label: 'Paragraph',        icon: '📝' },
  { type: 'multiple_choice', label: 'Multiple Choice', icon: '🔘' },
  { type: 'checkboxes',    label: 'Checkboxes',       icon: '☑️' },
  { type: 'dropdown',      label: 'Dropdown',         icon: '▾' },
  { type: 'rating',        label: 'Rating Scale',     icon: '⭐' },
  { type: 'date',          label: 'Date',             icon: '📅' },
  { type: 'number',        label: 'Number',           icon: '🔢' },
  { type: 'file_upload',   label: 'File Upload',      icon: '📎' },
  { type: 'section',       label: 'Section Break',    icon: '➖' },
  { type: 'hyperlink',     label: 'Hyperlink',        icon: '🔗' },
];

const newQuestion = (type = 'short_text') => ({
  id: crypto.randomUUID(),
  type,
  label: '',
  required: false,
  options: type === 'multiple_choice' || type === 'checkboxes' || type === 'dropdown'
    ? ['Option 1', 'Option 2']
    : [],
  ratingMax: type === 'rating' ? 5 : undefined,
  ratingLabels: type === 'rating' ? { low: '', high: '' } : undefined,
  placeholder: '',
  description: '',
  // file_upload-specific
  fileAccept: type === 'file_upload' ? '' : undefined,
  fileMaxMB: type === 'file_upload' ? 25 : undefined,
  fileMultiple: type === 'file_upload' ? false : undefined,
  // hyperlink-specific
  linkUrl: type === 'hyperlink' ? '' : undefined,
  linkText: type === 'hyperlink' ? '' : undefined,
});

const s = {
  page: { display: 'flex', height: 'calc(100vh - 56px)', margin: '-32px', overflow: 'hidden' },

  // Left toolbar
  toolbar: {
    width: 200,
    background: 'var(--ka-white)',
    borderRight: '1px solid var(--ka-gray-200)',
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
    flexShrink: 0,
    overflowY: 'auto',
  },
  toolbarTitle: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--ka-gray-400)',
    padding: '0 16px 8px',
  },
  qTypeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    fontSize: 14,
    color: 'var(--ka-gray-800)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'var(--font-body)',
    transition: 'background 0.1s',
    borderRadius: 0,
  },
  qTypeIcon: { fontSize: 14, width: 20, textAlign: 'center' },

  // Canvas
  canvas: {
    flex: 1,
    overflowY: 'auto',
    background: 'var(--ka-gray-50)',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },

  // Survey header card
  headerCard: {
    width: '100%',
    maxWidth: 700,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: 'var(--shadow-md)',
  },
  headerBanner: {
    height: 10,
    background: 'var(--ka-navy)',
  },
  headerBody: {
    background: 'var(--ka-white)',
    padding: '24px 28px',
    borderBottom: '4px solid var(--ka-navy)',
  },
  titleInput: {
    width: '100%',
    border: 'none',
    outline: 'none',
    fontFamily: 'var(--font-display)',
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--ka-navy)',
    background: 'transparent',
    marginBottom: 10,
  },
  descInput: {
    width: '100%',
    border: 'none',
    outline: 'none',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--ka-gray-600)',
    background: 'transparent',
    resize: 'none',
    lineHeight: 1.6,
  },

  // Question cards — NO overflow: 'hidden' here (it clips the expanded editor)
  questionCard: {
    width: '100%',
    maxWidth: 700,
    background: 'var(--ka-white)',
    borderRadius: 10,
    boxShadow: 'var(--shadow-sm)',
    border: '2px solid transparent',
    transition: 'border 0.15s',
    cursor: 'pointer',
  },
  questionCardActive: {
    border: '2px solid var(--ka-navy)',
  },
  questionCardInner: {
    padding: '20px 28px 28px',
  },
  questionHandle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  questionType: {
    fontSize: 12,
    color: 'var(--ka-gold)',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  addBtn: {
    width: '100%',
    maxWidth: 700,
    padding: '12px',
    background: 'rgba(26,39,68,0.06)',
    border: '2px dashed var(--ka-gray-200)',
    borderRadius: 10,
    fontSize: 13,
    color: 'var(--ka-gray-400)',
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    transition: 'all 0.15s',
  },

  // Right panel
  rightPanel: {
    width: 260,
    background: 'var(--ka-white)',
    borderLeft: '1px solid var(--ka-gray-200)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    overflowY: 'auto',
  },
  rightPanelHead: {
    padding: '16px',
    borderBottom: '1px solid var(--ka-gray-200)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rightPanelTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--ka-navy)',
  },

  // Top bar
  topBar: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    background: 'var(--ka-navy)',
    padding: '14px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  topBarTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 16,
    color: 'var(--ka-white)',
    fontWeight: 600,
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  btn: {
    padding: '9px 18px',
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    fontFamily: 'var(--font-body)',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  btnPrimary: {
    background: 'var(--ka-gold)',
    color: 'var(--ka-navy)',
  },
  btnSecondary: {
    background: 'rgba(255,255,255,0.15)',
    color: 'var(--ka-white)',
  },
  btnDanger: {
    background: 'rgba(239,68,68,0.15)',
    color: '#ef4444',
  },
  statusPill: {
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
};

export default function SurveyBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [title, setTitle] = useState('Untitled Survey');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([newQuestion('short_text')]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [status, setStatus] = useState('draft');
  const [settings, setSettings] = useState({
    authRequired: false,
    collectName: true,
    collectEmail: true,
    nameRequired: true,
    emailRequired: true,
    allowMultiple: false,
    showProgressBar: true,
    showLogo: true,
    confirmationMessage: 'Thank you for your response!',
    accessRoles: ['admin'],
    accentColor: '#1a2744',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [surveyId] = useState(id || crypto.randomUUID());
  const [activePanel, setActivePanel] = useState('question'); // 'question' | 'settings'
  const isNew = !id;

  // Load existing survey
  useEffect(() => {
    if (!id) return;
    getDoc(doc(db, 'surveys', id)).then(snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setTitle(d.title || '');
      setDescription(d.description || '');
      setQuestions(d.questions || []);
      setStatus(d.status || 'draft');
      setSettings(d.settings || settings);
    });
  }, [id]);

  const addQuestion = (type) => {
    const q = newQuestion(type);
    const updated = [...questions, q];
    setQuestions(updated);
    setActiveIdx(updated.length - 1);
    setActivePanel('question');
  };

  const updateQuestion = (idx, changes) => {
    const updated = [...questions];
    updated[idx] = { ...updated[idx], ...changes };
    setQuestions(updated);
  };

  const deleteQuestion = (idx) => {
    const updated = questions.filter((_, i) => i !== idx);
    setQuestions(updated);
    setActiveIdx(Math.max(0, idx - 1));
  };

  const moveQuestion = (idx, dir) => {
    const updated = [...questions];
    const target = idx + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    setQuestions(updated);
    setActiveIdx(target);
  };

  const duplicateQuestion = (idx) => {
    const copy = { ...questions[idx], id: crypto.randomUUID() };
    const updated = [...questions];
    updated.splice(idx + 1, 0, copy);
    setQuestions(updated);
    setActiveIdx(idx + 1);
  };

  const save = async (newStatus = status) => {
    setSaving(true);
    const cleanQuestions = questions.map(q => {
      const cleaned = {};
      Object.entries(q).forEach(([key, val]) => {
        if (val !== undefined) cleaned[key] = val;
      });
      return cleaned;
    });
    const cleanSettings = Object.fromEntries(
      Object.entries(settings).filter(([, v]) => v !== undefined)
    );
    const ref = doc(db, 'surveys', surveyId);
    const data = {
      id: surveyId,
      title,
      description,
      questions: cleanQuestions,
      settings: cleanSettings,
      status: newStatus,
      createdBy: user.uid,
      createdByName: profile?.displayName || user?.email || '',
      updatedAt: serverTimestamp(),
      ...(isNew ? { createdAt: serverTimestamp() } : {}),
    };
    await setDoc(ref, data, { merge: true });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    setStatus(newStatus);
    if (isNew) navigate(`/tools/surveys/edit/${surveyId}`, { replace: true });
  };

  const publish = () => save('active');
  const unpublish = () => save('draft');
  const close = () => save('closed');

  const publicUrl = `${window.location.origin}/s/${surveyId}`;

  return (
    <div style={{ ...s.page, flexDirection: 'column' }}>

      {/* Top action bar — outside the canvas so it never scrolls away */}
      <div style={{ ...s.topBar, borderRadius: 0, width: '100%', maxWidth: 'none', marginBottom: 0, flexShrink: 0 }}>
        <span style={s.topBarTitle}>{title || 'Untitled Survey'}</span>
        <span style={{
          ...s.statusPill,
          background: status === 'active' ? 'rgba(74,222,128,0.2)' : status === 'closed' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.15)',
          color: status === 'active' ? '#4ade80' : status === 'closed' ? '#ef4444' : 'rgba(255,255,255,0.6)',
        }}>
          {status}
        </span>
        <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => navigate('/tools/surveys')}>
          ← Back
        </button>
        <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => save()}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Draft'}
        </button>
        {status !== 'active' && (
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={publish}>
            Publish
          </button>
        )}
        {status === 'active' && (
          <button style={{ ...s.btn, ...s.btnDanger }} onClick={unpublish}>
            Unpublish
          </button>
        )}
      </div>

      {/* Body row: toolbar + canvas + right panel */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* Left toolbar — question types */}
      <div style={s.toolbar}>
        <div style={s.toolbarTitle}>Add Question</div>
        {QUESTION_TYPES.map(qt => (
          <button
            key={qt.type}
            style={s.qTypeBtn}
            onClick={() => addQuestion(qt.type)}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--ka-gray-100)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <span style={s.qTypeIcon}>{qt.icon}</span>
            {qt.label}
          </button>
        ))}

        <div style={{ borderTop: '1px solid var(--ka-gray-200)', margin: '12px 0' }} />
        <div style={s.toolbarTitle}>View</div>
        <button
          style={s.qTypeBtn}
          onClick={() => navigate(`/tools/surveys/results/${surveyId}`)}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--ka-gray-100)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <span style={s.qTypeIcon}>📊</span> Results
        </button>
        <button
          style={s.qTypeBtn}
          onClick={() => window.open(`/s/${surveyId}`, '_blank')}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--ka-gray-100)'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <span style={s.qTypeIcon}>👁️</span> Preview
        </button>
      </div>

      {/* Center canvas */}
      <div style={s.canvas}>

        {/* Share URL bar (when active) */}
        {status === 'active' && (
          <div style={{
            width: '100%', maxWidth: 700,
            background: 'var(--ka-white)',
            border: '1px solid rgba(200,151,58,0.4)',
            borderRadius: 10,
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ fontSize: 16 }}>🔗</span>
            <span style={{ fontSize: 12, color: 'var(--ka-gray-600)', fontWeight: 600, whiteSpace: 'nowrap' }}>Share URL:</span>
            <span style={{
              flex: 1, fontSize: 12, color: 'var(--ka-navy)',
              background: 'var(--ka-gray-50)', padding: '6px 10px', borderRadius: 6,
              fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{publicUrl}</span>
            <button
              style={{ ...s.btn, ...s.btnPrimary, padding: '6px 14px', fontSize: 12 }}
              onClick={() => { navigator.clipboard.writeText(publicUrl); }}
            >
              Copy
            </button>
          </div>
        )}

        {/* Survey header card */}
        <div style={s.headerCard}>
          <div style={{ ...s.headerBanner, background: settings.accentColor }} />
          <div style={s.headerBody}>
            <input
              style={s.titleInput}
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Survey Title"
            />
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Survey description — supports bold, italic, underline and line breaks"
              style={{ marginTop: 4 }}
            />
          </div>
        </div>

        {/* Questions */}
        {questions.map((q, idx) => (
          <div
            key={q.id}
            style={{
              ...s.questionCard,
              ...(activeIdx === idx ? s.questionCardActive : {}),
            }}
            onClick={() => { setActiveIdx(idx); setActivePanel('question'); }}
          >
            <div style={s.questionCardInner}>
              {activeIdx === idx ? (
                <QuestionEditor
                  key={q.id}
                  question={q}
                  active={true}
                  onChange={changes => updateQuestion(idx, changes)}
                  onDelete={() => deleteQuestion(idx)}
                  onMoveUp={() => moveQuestion(idx, -1)}
                  onMoveDown={() => moveQuestion(idx, 1)}
                  onDuplicate={() => duplicateQuestion(idx)}
                  isFirst={idx === 0}
                  isLast={idx === questions.length - 1}
                />
              ) : (
                <QuestionPreview key={q.id} question={q} />
              )}
            </div>
          </div>
        ))}

        {/* Add question button */}
        <button
          style={s.addBtn}
          onClick={() => addQuestion('short_text')}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--ka-gray-100)'; e.currentTarget.style.borderColor = 'var(--ka-gray-400)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(26,39,68,0.06)'; e.currentTarget.style.borderColor = 'var(--ka-gray-200)'; }}
        >
          + Add Question
        </button>

        <div style={{ height: 40 }} />
      </div>

      {/* Right panel */}
      <div style={s.rightPanel}>
        <div style={s.rightPanelHead}>
          <button
            style={{ ...s.rightPanelTitle, background: 'none', border: 'none', cursor: 'pointer', color: activePanel === 'question' ? 'var(--ka-navy)' : 'var(--ka-gray-400)', fontFamily: 'var(--font-body)' }}
            onClick={() => setActivePanel('question')}
          >Question</button>
          <button
            style={{ ...s.rightPanelTitle, background: 'none', border: 'none', cursor: 'pointer', color: activePanel === 'settings' ? 'var(--ka-navy)' : 'var(--ka-gray-400)', fontFamily: 'var(--font-body)' }}
            onClick={() => setActivePanel('settings')}
          >Settings</button>
        </div>

        {activePanel === 'settings' ? (
          <SurveySettingsPanel
            settings={settings}
            onChange={setSettings}
            title={title}
            onTitleChange={setTitle}
            description={description}
            onDescriptionChange={setDescription}
          />
        ) : (
          questions[activeIdx] && (
            <QuestionSidePanel
              question={questions[activeIdx]}
              onChange={changes => updateQuestion(activeIdx, changes)}
              allQuestions={questions}
            />
          )
        )}
      </div>
      </div>{/* end body row */}
    </div>
  );
}

// ── Right panel for a selected question ─────────────────────────
function QuestionSidePanel({ question, onChange, allQuestions }) {
  const sp = {
    wrap: { padding: 16 },
    label: {
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: 'var(--ka-gray-400)',
      marginBottom: 6, marginTop: 14, display: 'block',
    },
    select: {
      width: '100%', padding: '8px 10px', fontSize: 13,
      border: '1px solid var(--ka-gray-200)', borderRadius: 6,
      color: 'var(--ka-navy)', background: 'var(--ka-white)',
      fontFamily: 'var(--font-body)', outline: 'none',
    },
    toggle: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 0', borderBottom: '1px solid var(--ka-gray-100)',
    },
    toggleLabel: { fontSize: 13, color: 'var(--ka-gray-800)' },
    checkbox: { width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--ka-navy)' },
  };

  return (
    <div style={sp.wrap}>
      <span style={sp.label}>Question Type</span>
      <select
        style={sp.select}
        value={question.type}
        onChange={e => {
          const newType = e.target.value;
          const hasOptions = ['multiple_choice', 'checkboxes', 'dropdown'].includes(newType);
          onChange({
            type: newType,
            options: hasOptions ? (question.options?.length ? question.options : ['Option 1', 'Option 2']) : [],
            ratingMax: newType === 'rating' ? (question.ratingMax || 5) : undefined,
            fileAccept: newType === 'file_upload' ? (question.fileAccept ?? '') : undefined,
            fileMaxMB: newType === 'file_upload' ? (question.fileMaxMB ?? 25) : undefined,
            fileMultiple: newType === 'file_upload' ? (question.fileMultiple ?? false) : undefined,
            linkUrl: newType === 'hyperlink' ? (question.linkUrl ?? '') : undefined,
            linkText: newType === 'hyperlink' ? (question.linkText ?? '') : undefined,
          });
        }}
      >
        {QUESTION_TYPES.map(qt => (
          <option key={qt.type} value={qt.type}>{qt.icon} {qt.label}</option>
        ))}
      </select>

      <div style={sp.toggle}>
        <span style={sp.toggleLabel}>Required</span>
        <input
          type="checkbox"
          style={sp.checkbox}
          checked={question.required || false}
          onChange={e => onChange({ required: e.target.checked })}
        />
      </div>

      {question.type === 'rating' && (
        <>
          <span style={sp.label}>Max Rating</span>
          <select
            style={sp.select}
            value={question.ratingMax || 5}
            onChange={e => onChange({ ratingMax: Number(e.target.value) })}
          >
            {[3, 4, 5, 6, 7, 8, 9, 10].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span style={sp.label}>Low Label</span>
          <input
            style={{ ...sp.select, marginBottom: 4 }}
            value={question.ratingLabels?.low || ''}
            placeholder="e.g. Poor"
            onChange={e => onChange({ ratingLabels: { ...question.ratingLabels, low: e.target.value } })}
          />
          <span style={sp.label}>High Label</span>
          <input
            style={sp.select}
            value={question.ratingLabels?.high || ''}
            placeholder="e.g. Excellent"
            onChange={e => onChange({ ratingLabels: { ...question.ratingLabels, high: e.target.value } })}
          />
        </>
      )}

      {question.type === 'file_upload' && (
        <>
          <span style={sp.label}>Accepted File Types</span>
          <select
            style={sp.select}
            value={question.fileAccept || ''}
            onChange={e => onChange({ fileAccept: e.target.value })}
          >
            <option value="">Any file</option>
            <option value="image/*">Images only</option>
            <option value=".pdf">PDF only</option>
            <option value=".pdf,.doc,.docx">PDF & Word docs</option>
            <option value=".pdf,.doc,.docx,.xls,.xlsx">Documents & Spreadsheets</option>
            <option value="image/*,.pdf">Images & PDFs</option>
            <option value=".mp4,.mov,.avi">Videos</option>
          </select>

          <span style={sp.label}>Max File Size (MB)</span>
          <select
            style={sp.select}
            value={question.fileMaxMB || 10}
            onChange={e => onChange({ fileMaxMB: Number(e.target.value) })}
          >
            {[1, 2, 5, 10, 20, 25, 50].map(n => (
              <option key={n} value={n}>{n} MB</option>
            ))}
          </select>

          <div style={sp.toggle}>
            <span style={sp.toggleLabel}>Allow Multiple Files</span>
            <input
              type="checkbox"
              style={sp.checkbox}
              checked={question.fileMultiple || false}
              onChange={e => onChange({ fileMultiple: e.target.checked })}
            />
          </div>
        </>
      )}

      {question.type === 'hyperlink' && (
        <>
          <span style={sp.label}>URL</span>
          <input
            style={sp.select}
            value={question.linkUrl || ''}
            placeholder="https://example.com"
            onChange={e => onChange({ linkUrl: e.target.value })}
          />
          <span style={sp.label}>Link Text</span>
          <input
            style={sp.select}
            value={question.linkText || ''}
            placeholder="Click here (leave blank to show URL)"
            onChange={e => onChange({ linkText: e.target.value })}
          />
        </>
      )}

      <span style={sp.label}>Helper Text</span>
      <input
        style={sp.select}
        value={question.description || ''}
        placeholder="Optional helper text"
        onChange={e => onChange({ description: e.target.value })}
      />

      <span style={sp.label}>Placeholder</span>
      <input
        style={sp.select}
        value={question.placeholder || ''}
        placeholder="Placeholder text"
        onChange={e => onChange({ placeholder: e.target.value })}
      />

      {/* Section routing logic — only shown when this question is a section */}
      {question.type === 'section' && (
        <SectionLogicPanel section={question} onChange={onChange} allQuestions={allQuestions} />
      )}

      {/* Conditional logic — only for choice-based question types */}
      {['multiple_choice', 'checkboxes', 'dropdown', 'rating'].includes(question.type) && (
        <ConditionalLogicPanel question={question} onChange={onChange} allQuestions={allQuestions} />
      )}
    </div>
  );
}

// ── Section routing logic panel ──────────────────────────────────
// Shown in the side panel when the active question is a section.
// Rules shape: { sourceQuestionId, when, goto } where goto is a section id or '__end__'.
function SectionLogicPanel({ section, onChange, allQuestions }) {
  const sp = {
    label: {
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: 'var(--ka-gray-400)',
      marginBottom: 6, marginTop: 14, display: 'block',
    },
    select: {
      width: '100%', padding: '7px 10px', fontSize: 12,
      border: '1px solid var(--ka-gray-200)', borderRadius: 6,
      color: 'var(--ka-navy)', background: 'var(--ka-white)',
      fontFamily: 'var(--font-body)', outline: 'none',
    },
    ruleRow: {
      display: 'flex', flexDirection: 'column', gap: 4,
      background: 'var(--ka-gray-50)', borderRadius: 6,
      padding: '8px', marginBottom: 6,
      border: '1px solid var(--ka-gray-100)',
    },
    rowLabel: { fontSize: 11, color: 'var(--ka-gray-500)', fontWeight: 600 },
    addBtn: {
      width: '100%', padding: '7px', fontSize: 12, fontWeight: 600,
      background: 'none', border: '1px dashed var(--ka-gray-200)',
      borderRadius: 6, color: 'var(--ka-gray-500)', cursor: 'pointer',
      fontFamily: 'var(--font-body)', marginTop: 4,
    },
    removeBtn: {
      background: 'none', border: 'none', color: '#ef4444',
      cursor: 'pointer', fontSize: 14, padding: '0 2px', alignSelf: 'flex-end',
    },
  };

  const rules = section.logic || [];

  // Questions that appear BEFORE this section (in any section before this one)
  const thisSectionIdx = allQuestions.findIndex(q => q.id === section.id);
  const questionsBefore = allQuestions.slice(0, thisSectionIdx).filter(
    q => q.type !== 'section' && q.type !== 'hyperlink' &&
    ['multiple_choice', 'checkboxes', 'dropdown', 'rating'].includes(q.type)
  );

  // Target sections that come AFTER this one
  const targetSections = allQuestions.slice(thisSectionIdx + 1).filter(q => q.type === 'section');

  const setRule = (idx, changes) => {
    const updated = [...rules];
    updated[idx] = { ...updated[idx], ...changes };
    onChange({ logic: updated });
  };

  const addRule = () => {
    onChange({ logic: [...rules, { sourceQuestionId: questionsBefore[0]?.id || '', when: '', goto: '__end__' }] });
  };

  const removeRule = (idx) => {
    onChange({ logic: rules.filter((_, i) => i !== idx) });
  };

  // Get answer options for a given question id
  const getOpts = (qId) => {
    const q = allQuestions.find(x => x.id === qId);
    if (!q) return [];
    if (q.type === 'rating') return Array.from({ length: q.ratingMax || 5 }, (_, i) => String(i + 1));
    return q.options || [];
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 2 }}>
        <span style={{ ...sp.label, marginTop: 0 }}>Section Routing</span>
        {rules.length > 0 && (
          <span style={{ fontSize: 10, color: 'var(--ka-gold)', fontWeight: 700 }}>ACTIVE</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ka-gray-500)', marginBottom: 8, lineHeight: 1.4 }}>
        When the "Next" button is clicked on this section, route to a different section based on a previous answer.
      </div>

      {questionsBefore.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--ka-gray-400)', fontStyle: 'italic', marginBottom: 8 }}>
          No choice questions before this section yet.
        </div>
      )}

      {rules.map((rule, idx) => (
        <div key={idx} style={sp.ruleRow}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={sp.rowLabel}>IF question…</span>
            <button style={sp.removeBtn} onClick={() => removeRule(idx)} title="Remove rule">✕</button>
          </div>
          <select style={sp.select} value={rule.sourceQuestionId || ''} onChange={e => setRule(idx, { sourceQuestionId: e.target.value, when: '' })}>
            <option value="">— select question —</option>
            {questionsBefore.map(q => (
              <option key={q.id} value={q.id}>{stripHtml(q.label) || '(untitled)'}</option>
            ))}
          </select>
          <span style={sp.rowLabel}>…has answer…</span>
          <select style={sp.select} value={rule.when || ''} onChange={e => setRule(idx, { when: e.target.value })}>
            <option value="">— select answer —</option>
            {getOpts(rule.sourceQuestionId).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <span style={sp.rowLabel}>THEN go to…</span>
          <select style={sp.select} value={rule.goto || '__end__'} onChange={e => setRule(idx, { goto: e.target.value })}>
            <option value="__end__">End survey</option>
            {targetSections.map(q => (
              <option key={q.id} value={q.id}>Section: {stripHtml(q.label) || '(untitled section)'}</option>
            ))}
          </select>
        </div>
      ))}

      <button style={sp.addBtn} onClick={addRule} disabled={questionsBefore.length === 0}>+ Add routing rule</button>
    </>
  );
}

// ── Conditional logic panel ──────────────────────────────────────
function ConditionalLogicPanel({ question, onChange, allQuestions }) {
  const sp = {
    label: {
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: 'var(--ka-gray-400)',
      marginBottom: 6, marginTop: 14, display: 'block',
    },
    select: {
      width: '100%', padding: '7px 10px', fontSize: 12,
      border: '1px solid var(--ka-gray-200)', borderRadius: 6,
      color: 'var(--ka-navy)', background: 'var(--ka-white)',
      fontFamily: 'var(--font-body)', outline: 'none',
    },
    ruleRow: {
      display: 'flex', flexDirection: 'column', gap: 4,
      background: 'var(--ka-gray-50)', borderRadius: 6,
      padding: '8px', marginBottom: 6,
      border: '1px solid var(--ka-gray-100)',
    },
    rowLabel: { fontSize: 11, color: 'var(--ka-gray-500)', fontWeight: 600 },
    addBtn: {
      width: '100%', padding: '7px', fontSize: 12, fontWeight: 600,
      background: 'none', border: '1px dashed var(--ka-gray-200)',
      borderRadius: 6, color: 'var(--ka-gray-500)', cursor: 'pointer',
      fontFamily: 'var(--font-body)', marginTop: 4,
    },
    removeBtn: {
      background: 'none', border: 'none', color: '#ef4444',
      cursor: 'pointer', fontSize: 14, padding: '0 2px', alignSelf: 'flex-end',
    },
  };

  // For rating, options are 1..ratingMax
  const opts = question.type === 'rating'
    ? Array.from({ length: question.ratingMax || 5 }, (_, i) => String(i + 1))
    : (question.options || []);

  const rules = question.logic || []; // [{when: optionValue, goto: questionId | '__end__'}]

  const futureQuestions = (allQuestions || []).filter(q => q.id !== question.id && q.type !== 'hyperlink');

  const setRule = (idx, changes) => {
    const updated = [...rules];
    updated[idx] = { ...updated[idx], ...changes };
    onChange({ logic: updated });
  };

  const addRule = () => {
    onChange({ logic: [...rules, { when: opts[0] || '', goto: '__next__' }] });
  };

  const removeRule = (idx) => {
    onChange({ logic: rules.filter((_, i) => i !== idx) });
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 2 }}>
        <span style={{ ...sp.label, marginTop: 0 }}>Conditional Logic</span>
        {rules.length > 0 && (
          <span style={{ fontSize: 10, color: 'var(--ka-gold)', fontWeight: 700 }}>ACTIVE</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ka-gray-500)', marginBottom: 8, lineHeight: 1.4 }}>
        When a specific answer is chosen, skip to a different question.
      </div>

      {rules.map((rule, idx) => (
        <div key={idx} style={sp.ruleRow}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={sp.rowLabel}>IF answer is…</span>
            <button style={sp.removeBtn} onClick={() => removeRule(idx)} title="Remove rule">✕</button>
          </div>
          <select style={sp.select} value={rule.when || ''} onChange={e => setRule(idx, { when: e.target.value })}>
            <option value="">— select answer —</option>
            {opts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <span style={sp.rowLabel}>THEN go to…</span>
          <select style={sp.select} value={rule.goto || '__next__'} onChange={e => setRule(idx, { goto: e.target.value })}>
            <option value="__next__">Next question</option>
            <option value="__end__">End survey</option>
            {futureQuestions.map(q => (
              <option key={q.id} value={q.id}>
                {q.type === 'section'
                  ? `Section: ${stripHtml(q.label) || '(untitled section)'}`
                  : stripHtml(q.label) || '(untitled question)'}
              </option>
            ))}
          </select>
        </div>
      ))}

      <button style={sp.addBtn} onClick={addRule}>+ Add rule</button>
    </>
  );
}